import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchUtils, useLogin, useNotify } from 'react-admin';

import AuthLayout from '../../AuthLayout';
import MfaEnrollmentVerify from '../../../security/MfaEnrollmentVerify';
import MfaRecoveryCodes from '../../../security/MfaRecoveryCodes';
import forcedEnrollmentSteps, { deriveForcedEnrollmentStep } from './forcedEnrollmentSteps';

import config from '../../../../config';
import authRoutes from '../../../../constants/authRoutes';
import errorNotification from '../../../../utilities/errorNotification';
import { clockDriftStore, mfaChallengeStore } from '../../../../utilities/authTokenStore';
import hasClockDrift from '../../../../utilities/hasClockDrift';

const httpClient = fetchUtils.fetchJson;

type PendingEnrollment = {
  secret: string;
  enrollmentUri: string;
  pendingToken: string;
}

type ConfirmedEnrollment = {
  recoveryCodes: string[];
  token: string;
  refreshToken?: string;
}

const MfaForcedEnrollmentPage = () => {
  const [pendingEnrollment, setPendingEnrollment] = useState<PendingEnrollment | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedEnrollment | null>(null);
  const [startFailed, setStartFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const navigate = useNavigate();
  const started = useRef(false);

  const challengeToken = mfaChallengeStore.getToken();

  const backToSignIn = useCallback(() => {
    mfaChallengeStore.removeToken();
    navigate(authRoutes.LOGIN, { replace: true });
  }, [navigate]);

  const startEnrollment = useCallback(async () => {
    if (!challengeToken) return;

    setStartFailed(false);
    setLoading(true);
    try {
      const { json } = await httpClient(`${config.apiUrl}/auth/mfa/forced-enroll`, {
        method: 'POST',
        body: JSON.stringify({ challengeToken }),
      });
      setPendingEnrollment(json as PendingEnrollment);
    } catch (error) {
      setStartFailed(true);
      notify(errorNotification(error, 'Could not start the setup'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [challengeToken, notify, setLoading, setPendingEnrollment, setStartFailed]);

  const confirmEnrollment = useCallback(async (code: string) => {
    if (!challengeToken || !pendingEnrollment) return;

    setLoading(true);
    try {
      const { json } = await httpClient(`${config.apiUrl}/auth/mfa/enroll/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          challengeToken,
          pendingToken: pendingEnrollment.pendingToken,
          code,
        }),
      });
      const confirmedJson = json as ConfirmedEnrollment & { clockDriftSteps?: number };
      if (hasClockDrift(confirmedJson)) {
        clockDriftStore.setToken(String(confirmedJson.clockDriftSteps));
      }
      setConfirmed(confirmedJson);
    } catch (error) {
      notify(errorNotification(error, 'Could not verify the code'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [challengeToken, notify, pendingEnrollment, setConfirmed, setLoading]);

  const finishEnrollment = useCallback(async () => {
    if (!confirmed) return;

    const { token, refreshToken } = confirmed;
    await login({ token, refreshToken });
  }, [confirmed, login]);

  useEffect(() => {
    if (!challengeToken) {
      navigate(authRoutes.LOGIN, { replace: true });
      return;
    }
    if (started.current) return;

    started.current = true;
    startEnrollment();
  }, [challengeToken, navigate, startEnrollment]);

  if (!challengeToken) return null;

  const step = deriveForcedEnrollmentStep({
    hasRecoveryCodes: !!confirmed,
    hasPendingEnrollment: !!pendingEnrollment,
    startFailed,
  });

  return (
    <AuthLayout>
      <Typography component="h1" variant="h4" align="center" gutterBottom>
        Set up two-step verification
      </Typography>
      <Typography component="h2" variant="body1" align="center" sx={{ mb: 4 }}>
        Your organisation requires two-step verification.
        <br />
        Set it up now to finish signing in.
      </Typography>
      {step === forcedEnrollmentSteps.RECOVERY_CODES && confirmed && (
        <MfaRecoveryCodes
          centered
          recoveryCodes={confirmed.recoveryCodes}
          onAcknowledge={finishEnrollment}
        />
      )}
      {step === forcedEnrollmentSteps.PAIR && pendingEnrollment && (
        <MfaEnrollmentVerify
          centered
          loading={loading}
          secret={pendingEnrollment.secret}
          enrollmentUri={pendingEnrollment.enrollmentUri}
          confirmLabel="Verify and finish signing in"
          onConfirm={confirmEnrollment}
          onCancel={backToSignIn}
        />
      )}
      {step === forcedEnrollmentSteps.FAILED && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={startEnrollment}>Retry</Button>}
        >
          Could not start the setup. Signing in again gives you a fresh attempt.
        </Alert>
      )}
      {step === forcedEnrollmentSteps.STARTING && (
        <Box textAlign="center">
          <CircularProgress size={24} />
        </Box>
      )}
    </AuthLayout>
  );
};

export default MfaForcedEnrollmentPage;
